import { VectorTile, VectorTileFeature } from "@mapbox/vector-tile";
import { useQuery } from "@tanstack/react-query";
import bbox from "@turf/bbox";
import Pbf from "pbf";
import { Header, PMTiles } from "pmtiles";

import { getRanaApiV1 } from "@nens/rana/api-v1";

import { usePmTilesHeaderQuery } from "./query-pmtiles-header";
import { combineQueries } from "./utils/combine-queries";

export type RanaVectorTileFeature = VectorTileFeature & {
  bounds: [number, number, number, number];
};

// Helper: convert lon/lat to tile x/y at a given zoom
function lonLatToTile(lon: number, lat: number, zoom: number) {
  const x = Math.floor(((lon + 180) / 360) * Math.pow(2, zoom));
  const y = Math.floor(
    ((1 -
      Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) /
      2) *
      Math.pow(2, zoom)
  );
  return { x, y };
}

// Helper: get all tile x/y pairs covering bounds at zoom
function getTileRangeFromBounds(bounds: [number, number, number, number], zoom: number) {
  const [minLon, minLat, maxLon, maxLat] = bounds;
  const { x: xMin, y: yMax } = lonLatToTile(minLon, minLat, zoom);
  const { x: xMax, y: yMin } = lonLatToTile(maxLon, maxLat, zoom);
  return {
    xMin: Math.min(xMin, xMax),
    xMax: Math.max(xMin, xMax),
    yMin: Math.min(yMin, yMax),
    yMax: Math.max(yMin, yMax),
  };
}

const getPmTilesFeaturesQuery = (url: string | undefined, header: Header | undefined) => {
  return getRanaApiV1().getQueryOptions({
    ignoreTenant: true,
    queryKey: ["pm-tiles-features", url, header],
    queryFn: async (): Promise<RanaVectorTileFeature[] | undefined> => {
      if (!header || !url) return;

      const pmtiles = new PMTiles(url);
      const zoom = header.maxZoom;
      const bounds: [number, number, number, number] = [
        header.minLon,
        header.minLat,
        header.maxLon,
        header.maxLat,
      ];
      const { xMin, xMax, yMin, yMax } = getTileRangeFromBounds(bounds, zoom);

      const featureMap: Map<number, RanaVectorTileFeature> = new Map();

      // Helper: extract features from a single tile
      function extractFeaturesFromTile(tile: VectorTile, x: number, y: number, zoom: number) {
        for (const layerName in tile.layers) {
          const layer = tile.layers[layerName];
          for (let i = 0; i < layer.length; i++) {
            const feature = layer.feature(i);
            const uniqueId = feature.id;
            if (!featureMap.has(uniqueId)) {
              const geojson = feature.toGeoJSON(x, y, zoom);
              const bounds = bbox(geojson) as [number, number, number, number];
              // Create a new object with the same prototype as feature and add bounds
              const ranaFeature: RanaVectorTileFeature = {
                ...feature,
                bounds,
              } as RanaVectorTileFeature;
              Object.setPrototypeOf(ranaFeature, Object.getPrototypeOf(feature));
              featureMap.set(uniqueId, ranaFeature);
            }
          }
        }
      }

      // Helper: fetch and process a single tile
      async function processTile(zoom: number, x: number, y: number) {
        try {
          const response = await pmtiles.getZxy(zoom, x, y);
          if (response) {
            // Mapbox vector tile expects a Pbf instance
            const tile = new VectorTile(new Pbf(response.data) as any); // eslint-disable-line @typescript-eslint/no-explicit-any
            extractFeaturesFromTile(tile, x, y, zoom);
          }
        } catch (e) {
          // Ignore missing tiles
        }
      }

      for (let x = xMin; x <= xMax; x++) {
        for (let y = yMin; y <= yMax; y++) {
          await processTile(zoom, x, y);
        }
      }

      return Array.from(featureMap.values());
    },
    enabled: !!url && !!header,
  });
};

export const usePmTilesFeaturesQuery = (url?: string) => {
  const pmTilesHeaderQuery = usePmTilesHeaderQuery(url);
  const pmTilesFeaturesQuery = useQuery(getPmTilesFeaturesQuery(url, pmTilesHeaderQuery.data));
  return combineQueries(pmTilesFeaturesQuery, pmTilesHeaderQuery);
};
